import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { NAV } from "@/constants/testIds";

const links = [
  { label: "Work", href: "#work", id: NAV.linkWork },
  { label: "Services", href: "#services", id: NAV.linkServices },
  { label: "About", href: "#about", id: NAV.linkAbout },
  { label: "Contact", href: "#contact", id: NAV.linkContact },
];

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid={NAV.container}
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 0.61, 0.36, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[padding,background] duration-500 ${
        scrolled ? "py-3" : "py-6"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div
          className={`flex items-center justify-between rounded-full px-4 md:px-6 py-3 transition-colors duration-500 ${
            scrolled ? "glass shadow-[0_8px_32px_rgba(0,208,132,0.08)]" : "bg-transparent"
          }`}
        >
          <a
            href="#top"
            data-testid={NAV.logo}
            className="flex items-center gap-2.5 group"
            aria-label="Dove Infotech home"
          >
            <span className="relative w-11 h-11 shrink-0">
              <span
                aria-hidden
                className="absolute inset-0 rounded-full blur-md opacity-70"
                style={{
                  background:
                    "radial-gradient(circle, rgba(0,208,132,0.55), transparent 65%)",
                }}
              />
              <img
                src="/logo.png"
                alt="Dove Infotech logo"
                className="relative w-full h-full object-contain drop-shadow-[0_0_10px_rgba(0,208,132,0.45)]"
              />
            </span>
            <span className="font-display font-bold text-[15px] tracking-tight text-white">
              Dove<span className="text-emerald-400">.</span>Infotech
            </span>
          </a>

          <nav className="hidden md:flex items-center gap-9">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={l.id}
                className="link-underline text-[13px] uppercase tracking-[0.16em] text-white/75 hover:text-white transition-colors"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <a
            href="#contact"
            data-testid={NAV.ctaButton}
            className="hidden md:inline-flex items-center gap-2 rounded-full bg-emerald-400 text-emerald-950 px-5 py-2.5 text-[13px] font-medium hover:bg-emerald-300 transition-colors"
          >
            Start a project
            <span aria-hidden>→</span>
          </a>

          <button
            className="md:hidden inline-flex items-center justify-center w-10 h-10 rounded-full glass"
            aria-label="Toggle menu"
            onClick={() => setOpen((v) => !v)}
          >
            <div className="flex flex-col gap-[5px]">
              <span className={`block h-[1.5px] w-4 bg-white transition-transform ${open ? "translate-y-[3px] rotate-45" : ""}`} />
              <span className={`block h-[1.5px] w-4 bg-white transition-transform ${open ? "-translate-y-[3.5px] -rotate-45" : ""}`} />
            </div>
          </button>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="md:hidden mt-3 glass rounded-2xl p-6 flex flex-col gap-4"
            >
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  data-testid={`${l.id}-mobile`}
                  onClick={() => setOpen(false)}
                  className="text-white/85 text-lg font-display"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="#contact"
                onClick={() => setOpen(false)}
                className="mt-2 inline-flex justify-center rounded-full bg-emerald-400 text-emerald-950 px-5 py-3 text-sm font-medium"
              >
                Start a project →
              </a>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};
